import { SeeqNames } from 'generated-seeqnames/app.constants.seeqnames';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { sqWorkbenchStore } from '@/core/core.stores';
import { useFluxPath } from '@/core/hooks/useFluxPath.hook';
import { useModalManager } from '@/core/hooks/useModalManager.hook';
import { DatasourcesStatus } from '@/footer/DatasourcesStatus.organism';
import { MopinionGetFeedback } from '@/getFeedback/GetFeedback.atom';
import { HelpCenterModal } from '@/helpCenter/HelpCenterModal';
import { isAdmin, isTrustedUser } from '@/services/authorization.service';
import { seeqFullReleaseIdentifier } from '@/services/systemConfiguration.utilities';
import { SUPPORT_LINK } from '@/utilities/externalLinks';
import { subscribe } from '@/utilities/socket.utilities';
import { ButtonWithPopover, ExternalLink, Icon } from '@seeqdev/qomponents';

export interface RequestProgressMessage {
  consumption: string;
  serverLoad: {
    description: string;
  };
}

export const Footer = () => {
  const { t } = useTranslation();
  const [threadPoolUsage, setThreadPoolUsage] = useState('');
  const [showHelpCenterModal, setShowHelpCenterModal] = useModalManager(HelpCenterModal);
  const interactiveSessionId = useFluxPath(sqWorkbenchStore, () => sqWorkbenchStore.interactiveSessionId);
  const isDev = process.env.NODE_ENV === 'development';

  useEffect(() => {
    return subscribe({
      channelId: [SeeqNames.Channels.RequestsProgress, sqWorkbenchStore.interactiveSessionId],
      onMessage: (message: RequestProgressMessage) =>
        setThreadPoolUsage(`${message.serverLoad.description} ${message.consumption}`),
      useSubscriptionsApi: false,
    });
  }, [interactiveSessionId]);

  return (
    <footer id="footer" data-testid="footer" className="mainFooter flexColumnContainer pr10 hideInPdf">
      <MopinionGetFeedback />

      <div
        data-testid="feedback"
        className="cursorPointer sq-call-out pl10 pr10 pt4 pb4 mr10"
        onClick={() => setShowHelpCenterModal(true)}>
        <Icon icon="fa-comment-dots" extraClassNames="mr5" type="white" />
        {t('FEEDBACK')}
      </div>
      <div id="seeqVersion" className="inlineBlock pt4 pb4">
        {seeqFullReleaseIdentifier()} {isDev && '[DEV]'}
      </div>
      <div className="flexColumnContainer autoMarginLeft">
        {isTrustedUser() && <DatasourcesStatus />}

        {isAdmin() && !_.isEmpty(threadPoolUsage) && (
          <ButtonWithPopover
            placement="top"
            hasArrow
            tooltipPlacement="top"
            tooltip={t('THREAD_POOL_USAGE.TOOLTIP')}
            trigger={
              <div className="tw:whitespace-nowrap flexColumnContainer flexAlignCenter cursorPointer">
                <span className="tw:text-sm cursorDefault ml5 mr5">|</span>
                <span className="tw:text-sm pr5">{t('THREAD_POOL_USAGE.TITLE')}</span>
              </div>
            }>
            <div id="threadPoolUsagePopOver" className="datasourcesPopup min-width-500 pt10">
              <div>
                <div className="flexColumnContainer flexCenter">
                  <div className="text-pre-wrap text-monospace sq-text-color">{threadPoolUsage}</div>
                </div>
                <div className="text-center pt7 pb7">
                  <ExternalLink
                    href={`${SUPPORT_LINK}/troubleshooting-performance#TroubleshootingPerformance-ThreadPoolUsagePop-Up`}>
                    <span>{t('THREAD_POOL_USAGE.KB_LINK_DESCRIPTION')}</span>
                  </ExternalLink>
                </div>
              </div>
            </div>
          </ButtonWithPopover>
        )}
      </div>
      {showHelpCenterModal && <HelpCenterModal onClose={() => setShowHelpCenterModal(false)} />}
    </footer>
  );
};
